import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { EPath } from '@/router/path';
import { logout } from '@/service/auth';
import { EToastType } from '@/stores/feedback';
import { publishSignedOut } from '@/utils/authSync';
import { EAppErrorKind } from '@/utils/error';
import { useErrorHandler } from './useErrorHandler';
import { useToast } from './useToast';

/**
 * 主动登出。所有用户触发的登出都必须经这里，漏掉公告会让其他标签页停在失效会话上。
 * 它直接跳登录页，需要先征询用户的页面（编辑器）不能用它——那条走整文档跳转的 `/logout`。
 */
export const useSignOut = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { add: addToast } = useToast();
  const handleError = useErrorHandler();
  const [loading, setLoading] = useState(false);

  const signOut = useCallback(async () => {
    setLoading(true);
    // 本页不订阅自己的公告：根级 ErrorBoundary 那条路径上 ProtectLayout 已卸载，没有订阅方
    const leave = () => {
      publishSignedOut();
      void navigate(EPath.Login, { replace: true });
    };

    try {
      await logout();
      leave();
    } catch (error) {
      const exitFailed = () =>
        addToast({ content: t('file.management.exitfailed'), type: EToastType.ERROR, timeout: 2000 });

      handleError(error, {
        // 登出接口返回 401 说明会话本就失效,等价于登出成功:照旧公告,也不套用默认的跳登录策略
        [EAppErrorKind.UNAUTHORIZED]: leave,
        // 断网也提示「退出失败」,而不是全局默认的网络文案
        [EAppErrorKind.NETWORK]: exitFailed,
        default: exitFailed
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, handleError, navigate, t]);

  return { signOut, loading };
};
